import { Crown, Key, Edit3, UserPen, Eye, type LucideIcon } from 'lucide-react';

/**
 * Mapping of unified role-based permission levels to their corresponding Lucide React icons
 * This system uses these roles: owner/billing_admin/admin/author/contributor/viewer
 *
 * `owner` has to be here as well as in `normalizeRoleForDisplay`: the two are a
 * matched pair, and a surface that draws both — `TransferOwnershipModal` does —
 * would otherwise label someone "Owner" beside the viewer's eye icon, because
 * an unmapped role falls through to the `viewer` default.
 */
export const permissionLevelIconMap: Record<string, LucideIcon> = {
  'owner': Crown,
  'billing admin': Crown,
  'admin': Key,
  'author': Edit3,
  'contributor': UserPen,
  'viewer': Eye,
} as const;

/**
 * Type definition for unified permission levels
 */
export type PermissionLevel = keyof typeof permissionLevelIconMap;

/**
 * Helper function to normalize role values for icon mapping
 * Converts database role values to UI display values
 */
export function normalizeRoleForIcon(role: string): PermissionLevel {
  const normalizedRole = role.toLowerCase().trim();
  
  // Map 'billing_admin' to 'billing admin' for UI display
  if (normalizedRole === 'billing_admin') {
    return 'billing admin';
  }
  
  // Ensure the role exists in our mapping, default to 'viewer'
  if (normalizedRole in permissionLevelIconMap) {
    return normalizedRole as PermissionLevel;
  }
  
  return 'viewer';
}

/**
 * Helper function to normalize role values for display text
 * Converts 'billing_admin' to 'Billing Admin' and properly capitalizes other roles
 */
export function normalizeRoleForDisplay(role: string): string {
  const normalizedRole = role.toLowerCase().trim();
  
  switch (normalizedRole) {
    // 'owner' had no case, so it fell through and rendered as raw lowercase
    // beside a properly-cased 'Admin'. Nothing hit it while dashboard only
    // ever displayed its app-access role, which signup writes as 'admin' for
    // everyone; the account page now shows the org role, which does say owner.
    case 'owner':
      return 'Owner';
    case 'billing_admin':
      return 'Billing Admin';
    case 'admin':
      return 'Admin';
    case 'author':
      return 'Author';
    case 'contributor':
      return 'Contributor';
    case 'viewer':
      return 'Viewer';
    default:
      return role;
  }
}
